import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Truck, Package2, Map, Fuel,
  BookUser,
  Archive, FolderOpen, Calculator, BarChart3, Settings, LogOut,
  ChevronDown, ChevronRight, Store, Layers, Car, UserCheck,
  PackageSearch, TruckIcon, BookOpen, ClipboardList, CheckSquare, MessageSquare,
  Clock, CheckCircle2
} from 'lucide-react';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookingsOpen, setBookingsOpen] = useState(
    location.pathname.startsWith('/booking')
  );

  const [fleetOpen, setFleetOpen] = useState(
    (location.pathname.startsWith('/fleet') && !location.pathname.startsWith('/fleet/vendors')) || location.pathname.startsWith('/dispatch')
  );
  const [reportsOpen, setReportsOpen] = useState(location.pathname.startsWith('/reports'));

  const isActive = (path) => {
    if (path === '/booking') return location.pathname.startsWith('/booking') || location.pathname.startsWith('/bookings');
    if (path === '/estimates') return location.pathname.startsWith('/estimates');
    if (path === '/dockets') return location.pathname.startsWith('/dockets');
    if (path === '/vendors') return location.pathname.startsWith('/vendors') || location.pathname.startsWith('/fleet/vendors');
    return location.pathname === path;
  };
  const isParentActive = (prefix) => {
    if (prefix === '/fleet') {
      return location.pathname.startsWith('/fleet') || location.pathname.startsWith('/dispatch');
    }
    return location.pathname.startsWith(prefix);
  };




  const fleetSubItems = [
    { path: '/fleet/dispatch', icon: Map, label: 'Dispatch Control Tower' },
    { path: '/fleet/vehicle-types', icon: Layers, label: 'Vehicle Type Master' },
    { path: '/fleet/vehicles', icon: Car, label: 'Vendor Vehicles' },
    { path: '/fleet/drivers', icon: UserCheck, label: 'Drivers' },
    { path: '/fleet/trips', icon: Map, label: 'Trip Management' },
    { path: '/fleet/driver-ledger', icon: BookUser, label: 'Driver Ledger' },
  ];

  const reportsSubItems = [
    { path: '/reports/inventory', icon: PackageSearch, label: 'Inventory Reports' },
    { path: '/reports/fleet', icon: TruckIcon, label: 'Fleet Reports' },
    { path: '/reports/dockets', icon: ClipboardList, CheckSquare, label: 'Docket Reports' },
    { path: '/reports/bookings', icon: BookOpen, label: 'Booking Reports' },
  ];

  const topMenuItemsBefore = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/customers', icon: Users, label: 'Customers' },
  ];

  const topMenuItemsAfter = [
    { path: '/tasks', icon: CheckSquare, label: 'Task Management (TMS)' },
    { isChat: true, icon: MessageSquare, label: 'Team Chat (Slack)' },
  ];

  const bottomMenuItems = [
    { path: '/inventory', icon: Archive, label: 'Inventory' },
    { path: '/documents', icon: FolderOpen, label: 'Documents' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="w-[280px] bg-primary-blue h-screen fixed left-0 top-0 flex flex-col shadow-xl">
      {/* Logo Section */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-blue rounded-lg flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold font-manrope">LogisticsHub</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="w-2 h-2 bg-accent-green rounded-full"></div>
              <span className="text-white/70 text-xs font-ibm">Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {/* Top items (Dashboard, Customers) */}
        {topMenuItemsBefore.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
                isActive(item.path) ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                <span className="font-ibm text-sm font-medium">{item.label}</span>
              </div>
            </Link>
          );
        })}

        {/* Bookings — direct link */}
        <Link
          to="/booking"
          data-testid="nav-bookings"
          className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
            isActive('/booking') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5" />
            <span className="font-ibm text-sm font-medium">Bookings</span>
          </div>
        </Link>

        {/* Dockets — direct link */}
        <Link
          to="/dockets"
          data-testid="nav-dockets"
          className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
            isActive('/dockets') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <Package2 className="w-5 h-5" />
            <span className="font-ibm text-sm font-medium">Dockets</span>
          </div>
        </Link>

        {/* Estimates — direct link (below Dockets) */}
        <Link
          to="/estimates"
          data-testid="nav-estimates"
          className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
            isActive('/estimates') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5" />
            <span className="font-ibm text-sm font-medium">Estimates</span>
          </div>
        </Link>

        {/* Vendors — direct link to /vendors */}
        <Link
          to="/vendors"
          data-testid="nav-vendors"
          className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
            isActive('/vendors') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <Store className="w-5 h-5" />
            <span className="font-ibm text-sm font-medium">Vendors</span>
          </div>
        </Link>

        {/* Top items after Bookings (Tasks, Team Chat) */}
        {topMenuItemsAfter.map((item) => {
          const Icon = item.icon;
          if (item.isChat) {
            return (
              <button
                key="team-chat"
                onClick={() => window.dispatchEvent(new CustomEvent('open-chat'))}
                data-testid="nav-team-chat"
                className="w-full flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all text-white/80 hover:bg-accent-blue/20 hover:text-white group"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-emerald-400 group-hover:scale-110 transition-transform" />
                  <span className="font-ibm text-sm font-medium">{item.label}</span>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </button>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
                isActive(item.path) ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                <span className="font-ibm text-sm font-medium">{item.label}</span>
              </div>
            </Link>
          );
        })}

        {/* Fleet Management — expandable dropdown */}
        <div className="mb-1">
          <button
            data-testid="nav-fleet-management"
            onClick={() => setFleetOpen(prev => !prev)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
              isParentActive('/fleet') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5" />
              <span className="font-ibm text-sm font-medium">Fleet Management</span>
            </div>
            {fleetOpen
              ? <ChevronDown className="w-4 h-4 transition-transform" />
              : <ChevronRight className="w-4 h-4 transition-transform" />
            }
          </button>

          {/* Sub-items */}
          {fleetOpen && (
            <div className="ml-4 mt-1 border-l border-white/20 pl-3 space-y-1">
              {fleetSubItems.map((sub) => {
                const Icon = sub.icon;
                const subActive = location.pathname === sub.path ||
                  (sub.path === '/fleet/dispatch' && (location.pathname === '/dispatch' || location.pathname === '/fleet/dispatch')) ||
                  (sub.path === '/fleet/vehicle-types' && location.pathname.startsWith('/fleet/vehicle-types')) ||
                  (sub.path === '/fleet/vehicles' && location.pathname.startsWith('/fleet/vehicles')) ||
                  (sub.path === '/fleet/drivers' && location.pathname.startsWith('/fleet/drivers')) ||
                  (sub.path === '/fleet/trips' && location.pathname.startsWith('/fleet/trips')) ||
                  (sub.path === '/fleet/driver-ledger' && location.pathname.startsWith('/fleet/driver-ledger'));
                return (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    data-testid={`nav-fleet-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                      subActive ? 'bg-white/20 text-white font-medium' : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-ibm">{sub.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Reports — expandable dropdown */}
        <div className="mb-1">
          <button
            data-testid="nav-reports"
            onClick={() => setReportsOpen(prev => !prev)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
              isParentActive('/reports') ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5" />
              <span className="font-ibm text-sm font-medium">Reports</span>
            </div>
            {reportsOpen
              ? <ChevronDown className="w-4 h-4 transition-transform" />
              : <ChevronRight className="w-4 h-4 transition-transform" />
            }
          </button>
          {reportsOpen && (
            <div className="ml-4 mt-1 border-l border-white/20 pl-3 space-y-1">
              {reportsSubItems.map((sub) => {
                const Icon = sub.icon;
                const subActive = location.pathname.startsWith(sub.path);
                return (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    data-testid={`nav-reports-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                      subActive ? 'bg-white/20 text-white font-medium' : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-ibm">{sub.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom items */}
        {bottomMenuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-all ${
                isActive(item.path) ? 'bg-accent-blue text-white' : 'text-white/80 hover:bg-accent-blue/20 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                <span className="font-ibm text-sm font-medium">{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Logout Section */}
      <div className="p-4 border-t border-white/10">
        <button
          data-testid="logout-button"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-accent-red/20 hover:text-white transition-all w-full"
          onClick={() => window.location.href = '/'}
        >
          <LogOut className="w-5 h-5" />
          <span className="font-ibm text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
